import {Component, NgZone, OnInit, ViewEncapsulation} from '@angular/core';
import {DevicesService, LangService, LoaderService, NavigateService, ShortcutAction} from 'ngx-satoris';
import {ApiService} from '../../shared/services/api.service';
import {DocumentType, UserPermission, UserPlatformRole} from '../../shared/models/user';
import {UiPermission} from 'src/app/shared/models/permission';
import {hasUiPerm} from 'src/app/shared/models/default-filter-strategy';
import {documentTypeIsPassport, documentTypeIsVisa} from 'src/app/shared/models/document-type-helpers';
import {CartService} from '../../shared/services/cart.service';
import {environment} from 'src/environments/environment';
import {RequestService} from '../../shared/services/request.service';
import {ScanDocumentService} from 'src/app/shared/services/scan-document.service';
import {InactivityService} from '../../shared/services/inactivity.service';
import {ConsumeMode, RequestState} from 'src/app/shared/models/request';
import {FormGroup, FormBuilder} from '@angular/forms';
import {Subscription} from 'rxjs';
import {useReturnUrl} from 'src/app/shared/services/application.service';
import {Router} from '@angular/router';
import {isJsonParsable} from 'src/app/shared/utils/parseMetadata';
import {QueueService} from '../../shared/services/queue.service';

declare const window: any;
const permissions = window.cordova?.plugins?.permissions;

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class DashboardComponent implements OnInit {

  userRoles = UserPlatformRole;
  form: FormGroup;
  previousIdApplication = '';
  changeConsumeSubscription : Subscription;

  constructor(public nav: NavigateService,
              public api: ApiService,
              public cart: CartService,
              public scanDocument: ScanDocumentService,
              public request: RequestService,
              private formBuilder: FormBuilder,
              private loader: LoaderService,
              private lang: LangService,
              private deviceService: DevicesService,
              private inactivity: InactivityService,
              private queue: QueueService,
              private zone: NgZone,
              private router: Router) {
    if(this.inactivity.isWatching) this.inactivity.stopWatching();
  }

  ngOnInit() {
    this.loader.loading(false);
    if(!this.form) {
      this.form = this.formBuilder.group({
        entryConsume: ['', []],
        exitConsume: ['', []],
        transitConsume: ['', []]
      });
    }
    if(!this.changeConsumeSubscription) {
      this.changeConsumeSubscription = this.scanDocument.consumeMode$.subscribe((mode) => {
        if(!this.form) return;
        this.form.get('entryConsume').setValue(mode === ConsumeMode.ENTRY);
        this.form.get('exitConsume').setValue(mode === ConsumeMode.EXIT);
        this.form.get('transitConsume').setValue(mode === ConsumeMode.TRANSIT);
      });
    }
    this.queue.onNavDeleteOfflineQueueId = undefined;
    this.scanDocument.resetScanDatas();
    const returnUrl = isJsonParsable(localStorage.getItem('return_url'));
    if(returnUrl) {
      return useReturnUrl(this.router, returnUrl);
    }

    const webKiosk = sessionStorage.getItem('webKiosk') === 'true';
    if(this.api.userRole.isKiosk && !webKiosk) {
      this.nav.to('standby-kiosk');
    }
    if(this.api.hasPerm(UserPermission.ALLOW_CONTROL) && !this.api.userRole?.isCustomer) {
      this.api.place(this.api.userPlaceId).then(res => {
        this.api.connectDailyOtp = res.connectDailyOtp ? res.connectDailyOtp : undefined;
      });
    }
    if(this.api.userRole.isWorker && this.api.hasPerm(this.api.perm.ALLOW_CONSUME) && !this.api.checkSeniority()) {
      this.initConsumeModeFromStorage();
    }
    if(this.api.isElectronApp && this.api.env.enableWelcomeFlow) {
      electron.step('default', false, []);
    }

    if(this.api.env.useGuichet) this.request.checkIfNoTimeslot();
    this.request.checkIfPendingWaiting();
    this.queue.cleanOfflineOrphans();
    this.previousIdApplication = window.sessionStorage.getItem('previousIdApplication');
  }

  ngAfterViewInit(): void {
    const webKiosk = sessionStorage.getItem('webKiosk') === 'true';
    if(webKiosk && this.api.userRole?.isKiosk) {
      this.cart.startApplication(true);
    }
  }

  ngOnDestroy() {
    if(this.changeConsumeSubscription) {
      this.changeConsumeSubscription.unsubscribe();
    }
  }

  offlineLoader() {
    this.loader.loading(true, {type: 'warn', message: this.lang.transform('no.connection')});
  }

  clickScan() {
    if(this.deviceService.isDevices('cordova')) {
      permissions.checkPermission(permissions.CAMERA, (status: any) =>{
        if(status.hasPermission) {
          this.zone.run(() => {
            this.nav.to('scan-qr');
          });
        } else {
          this.zone.run(() => {
            this.nav.to('page-permission');
          });
        }
      });
    } else {
      this.nav.to('scan-qr');
    }
  }

  disconnect() {
    this.loader.loading(true, {type: 'warn', message: 'ask.disconnect', btnLabel: 'global.yes'}).then((done) => {
      if(done) {
        this.api.signOut();
      }
    });
  }

  private initConsumeModeFromStorage() {
    const consumeMode = localStorage.getItem('consumeMode') as ConsumeMode;
    if(consumeMode === ConsumeMode.ENTRY || consumeMode === ConsumeMode.EXIT || consumeMode === ConsumeMode.TRANSIT) {
      this.scanDocument.changeConsume(consumeMode, false);
    }
  }

  goToPreviousApplication() {
    this.previousIdApplication = window.sessionStorage.getItem('previousIdApplication');
    if(this.previousIdApplication) {
      this.nav.to('admin-request-select/' + this.previousIdApplication, undefined, {queryParams: {from: 'dashboard'}});
    } else {
      this.loader.loading(true, {type: 'error', message: this.lang.transform('dashboard.noPreviousApplication')});
    }
  }

  nextApplication(): void {
    if(!this.api.userRole.isAdmin && !(this.api.userRole.isWorker && this.api.hasPerm(this.api.perm.ALLOW_CONTROL))) {
      this.loader.loading(true, {
        type: 'error',
        message: this.lang.transform('error.insufficientPermissions')
      });
      return;
    }

    const searchParams = {
      state: [
        RequestState.PAID_NOT_CONFIRMED,
        RequestState.DELAY_PAY_NOT_CONFIRMED
      ].map(s => s.toString()),
      operationIds: this.api.userInfo.server.paymentRequests.map(doc => doc.id) as any,
      registrationPortId: this.api.userRole?.isWorker ? this.api.userPlaceId : undefined
    };

    this.api.searchPayment(searchParams, 'createdAt', 'ASC', 0, 1).then(result => {
      if(result?.result && result.result.length > 0) {
        const nextRequest = result.result[0];
        this.nav.to('admin-request-select/' + nextRequest.id, undefined, {queryParams: {from: 'dashboard'}});
      } else {
        this.loader.loading(true, {
          type: 'info',
          message: this.lang.transform('nextApplication.noMore')
        });
      }
    }).catch(error => {
      console.error('Error fetching next request:', error);
      this.loader.loading(true, {
        type: 'error',
        message: this.lang.transform('nextApplication.error')
      });
    });
  }

  showNextApplicationButton(): boolean {
    return this.api.env.nextApplicationButton &&
           (this.api.userRole.isAdmin || (this.api.userRole.isWorker && this.api.hasPerm(this.api.perm.ALLOW_CONTROL)));
  }

  protected readonly environment = environment;
  protected readonly DocumentType = DocumentType;
  protected readonly UiPermission = UiPermission;
  protected readonly ConsumeMode = ConsumeMode;
  protected readonly ShortcutAction = ShortcutAction;
  documentTypeIsVisa = documentTypeIsVisa;
  documentTypeIsPassport = documentTypeIsPassport;
  hasUiPerm = hasUiPerm;
}
